import React, { JSX, useState } from "react";
import {
  Typography,
  Box,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  Radio
} from "@mui/material";
import produce from "immer";
import Tooltip from "@mui/material/Tooltip";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { ReverseMap } from "../../../../helpers/types";
import HtmlTooltip from "../../../../components/HtmlTooltip";
import { SORT_ORDER, SortOrderType } from "../../../Assessment/AssessmentForm/OPDComponents";
import EditableTable, { TableState } from "../../../../components/EditableTable/EditableTable";
import { AssessmentSettings, ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";

export enum ORDER_ACTION {
  UP = "up",
  DOWN = "down"
}

export type OrderActionType = ReverseMap<typeof ORDER_ACTION>;

export const OrderButtons = ({
  onClick,
  isDisabled
}: {
  isDisabled: {
    up: boolean;
    down: boolean;
  };
  onClick: {
    up: () => void;
    down: () => void;
  };
}): JSX.Element => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5
    }}
  >
    <IconButton
      title="Move upward"
      onClick={onClick.up}
      disabled={isDisabled.up}
      sx={{
        border: 1,
        borderColor: "divider",
        color: "primary.main",
        borderRadius: "10px"
      }}
    >
      <ArrowUpwardIcon />
    </IconButton>

    <IconButton
      title="Move downward"
      onClick={onClick.down}
      disabled={isDisabled.down}
      sx={{
        border: 1,
        borderColor: "divider",
        color: "primary.main",
        borderRadius: "10px"
      }}
    >
      <ArrowDownwardIcon />
    </IconButton>
  </Box>
);

interface LabelInputFieldProps {
  value: string;
  onFocus: () => void;
  onChange: (e: any) => void;
  disabled?: boolean;
}

const LabelInputField = ({ value, disabled = false, onFocus, onChange }: LabelInputFieldProps) => (
  <TextField
    variant="outlined"
    value={value}
    onFocus={onFocus}
    onChange={onChange}
    disabled={disabled}
    sx={{ flex: 1 }}
  />
);

interface PropType {
  resourceCentre: ResourceCentre;
  orderedComponentLabels: SortOrderType[];
  setOrderedComponentLabels: React.Dispatch<React.SetStateAction<any>>;
  assessmentSettings: AssessmentSettings;
  setAssessmentSettings: React.Dispatch<React.SetStateAction<AssessmentSettings>>;
}

const EyeComponents = [
  { value: "visualAcuity", label: "Visual Acuity" },
  { value: "autoRefraction", label: "Auto Refraction" },
  { value: "refraction", label: "Refraction" },
  { value: "subjectiveRefraction", label: "Subjective Refraction" },
  { value: "fieldOfVision", label: "Field of Vision" },
  { value: "colorVision", label: "Color Vision" },
  { value: "anteriorSegment", label: "Anterior Segment" },
  { value: "posteriorSegment", label: "Posterior Segment" },
  { value: "glassPrescription", label: "Glass Prescription" },
  { value: "lensPrescription", label: "Lens Prescription" }
] as const;

const AssessmentLabels = ({
  resourceCentre,
  orderedComponentLabels,
  setOrderedComponentLabels,
  assessmentSettings,
  setAssessmentSettings
}: PropType): JSX.Element => {
  const [showTable, setShowTable] = useState(
    resourceCentre.settings.assessmentSettings.components.tabular
  );
  const [showEye, setShowEye] = useState(resourceCentre.settings.assessmentSettings.components.eye);
  const [errorMessage, setErrorMessage] = React.useState("");

  const moveItem = (fromIndex: number, toIndex: number) => {
    setOrderedComponentLabels((items: SortOrderType[]) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= items.length ||
        toIndex >= items.length ||
        fromIndex === toIndex
      ) {
        return items;
      }

      const newItems = [...items];
      const activeId = newItems[fromIndex];
      const isLabLabel = activeId === SORT_ORDER.LAB_TEST;

      if (isLabLabel) {
        newItems.splice(fromIndex, 1);
        const serviceIdx = newItems.indexOf(SORT_ORDER.SERVICE);
        if (serviceIdx !== -1) newItems.splice(serviceIdx, 1);

        let insertIndex = toIndex;
        if (insertIndex > fromIndex) insertIndex -= 1;
        if (insertIndex < 0) insertIndex = 0;
        if (insertIndex > newItems.length) insertIndex = newItems.length;

        newItems.splice(insertIndex, 0, activeId, SORT_ORDER.SERVICE);
        return newItems;
      }

      const item = newItems.splice(fromIndex, 1)[0];
      newItems.splice(toIndex, 0, item);

      const labTestIdx = newItems.indexOf(SORT_ORDER.LAB_TEST);
      const serviceIdx = newItems.indexOf(SORT_ORDER.SERVICE);
      if (labTestIdx !== -1 && serviceIdx !== -1 && serviceIdx !== labTestIdx + 1) {
        newItems.splice(serviceIdx, 1);
        const newLabTestIdx = newItems.indexOf(SORT_ORDER.LAB_TEST);
        newItems.splice(newLabTestIdx + 1, 0, SORT_ORDER.SERVICE);
      }

      return newItems;
    });
  };

  const moveUp = (componentLabel: SortOrderType) => {
    const index = orderedComponentLabels.indexOf(componentLabel);
    if (index <= 0) return;
    moveItem(index, index - 1);
  };

  const moveDown = (componentLabel: SortOrderType) => {
    const index = orderedComponentLabels.indexOf(componentLabel);
    if (index === -1 || index >= orderedComponentLabels.length - 1) return;
    moveItem(index, index + 1);
  };

  const canMoveUp = (componentLabel: SortOrderType) => {
    const index = orderedComponentLabels.indexOf(componentLabel);
    return index > 0;
  };

  const canMoveDown = (componentLabel: SortOrderType) => {
    const index = orderedComponentLabels.indexOf(componentLabel);
    return index !== -1 && index < orderedComponentLabels.length - 1;
  };

  return (
    <Box>
      {errorMessage && (
        <Typography color="red" my={2}>
          {errorMessage}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          py: 1,
          pr: "10px",
          gap: 2,
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <Typography variant="body1" sx={{ flex: 1, fontWeight: "bold" }}>
          Components
        </Typography>
        <Typography variant="body1" sx={{ flex: 1, fontWeight: "bold" }}>
          Labels
        </Typography>
        <Typography variant="body1" sx={{ width: "100px", fontWeight: "bold" }}>
          Sort
        </Typography>
        <Typography variant="body1" sx={{ width: "50px", fontWeight: "bold" }}>
          Print
        </Typography>
      </Box>

      <Box
        sx={{
          height: "30rem",
          overflowY: "auto",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "10px"
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderRadius: "999px"
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.35)"
          }
        }}
      >
        {orderedComponentLabels.map((componentLabel) => {
          if (componentLabel === SORT_ORDER.TABULAR) {
            return (
              <Box key={componentLabel}>
                <Box sx={{ display: "flex", my: 1, gap: 2 }}>
                  <FormControlLabel
                    sx={{ flex: 1 }}
                    label="Tabular Component"
                    control={
                      <Checkbox
                        checked={assessmentSettings.components.tabular}
                        onChange={() => {
                          setShowTable(!assessmentSettings.components.tabular);
                          const newState = produce(assessmentSettings, (draft) => {
                            draft.components.tabular = !assessmentSettings.components.tabular;
                          });
                          setAssessmentSettings(newState);
                        }}
                      />
                    }
                  />

                  <LabelInputField
                    value={assessmentSettings.tabularComponent.title}
                    onFocus={() => setErrorMessage("")}
                    onChange={(e) => {
                      if (e.target.value.length > 40) {
                        setErrorMessage("**Label names can not exceed more than 40 characters");
                        return;
                      }
                      setErrorMessage("");
                      setAssessmentSettings(() =>
                        produce(assessmentSettings, (draft) => {
                          draft.tabularComponent.title = e.target.value;
                        })
                      );
                    }}
                  />

                  <Box sx={{ width: "100px" }}>
                    <OrderButtons
                      isDisabled={{
                        up: !canMoveUp(componentLabel),
                        down: !canMoveDown(componentLabel)
                      }}
                      onClick={{
                        up: () => moveUp(componentLabel),
                        down: () => moveDown(componentLabel)
                      }}
                    />
                  </Box>

                  <Checkbox
                    sx={{ width: "50px" }}
                    checked={assessmentSettings.showComponentsInPrint?.tabular ?? true}
                    onChange={() => {
                      const newState = produce(assessmentSettings, (draft) => {
                        if (!draft.showComponentsInPrint) {
                          draft.showComponentsInPrint = {} as any;
                        }
                        draft.showComponentsInPrint.tabular =
                          !assessmentSettings.showComponentsInPrint?.tabular;
                      });
                      setAssessmentSettings(newState);
                    }}
                  />
                </Box>

                {showTable && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      gap: 2,
                      p: 1,
                      my: 1,
                      ml: 3.75,
                      flexDirection: "column",
                      alignItems: "flex-start",
                      component: "fieldset",
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: "0.23rem"
                    }}
                  >
                    <Typography px={2} py={1}>
                      Customize Tabular Component
                    </Typography>
                    <EditableTable
                      maxColCount={10}
                      onChange={(value: TableState) => {
                        const newState = produce(assessmentSettings, (draft) => {
                          draft.tabularComponent = value;
                        });
                        setAssessmentSettings(newState);
                      }}
                      initValue={assessmentSettings.tabularComponent}
                      hideControls={false}
                      hideLabel
                    />
                  </Box>
                )}
              </Box>
            );
          }

          if (componentLabel === SORT_ORDER.ATTACHMENTS) {
            return (
              <Box key={componentLabel} sx={{ display: "flex", my: 1, gap: 2 }}>
                <FormControlLabel
                  sx={{
                    flex: 1,
                    "& .MuiFormControlLabel-label.Mui-disabled": { color: "text.primary" }
                  }}
                  label={assessmentSettings.componentLabels[componentLabel]?.defaultLabel}
                  control={
                    <Checkbox
                      disabled
                      checked={assessmentSettings.components.attachments}
                      onChange={() => null}
                    />
                  }
                />

                <LabelInputField
                  disabled={!assessmentSettings.components.attachments}
                  value={
                    assessmentSettings.componentLabels[componentLabel]?.customLabel ||
                    assessmentSettings.componentLabels[componentLabel]?.defaultLabel
                  }
                  onFocus={() => setErrorMessage("")}
                  onChange={(e) => {
                    if (e.target.value.length > 40) {
                      setErrorMessage("**Label names can not exceed more than 40 characters");
                      return;
                    }
                    setErrorMessage("");
                    setAssessmentSettings(() =>
                      produce(assessmentSettings, (draft) => {
                        draft.componentLabels.attachments = {
                          ...assessmentSettings.componentLabels.attachments,
                          customLabel: e.target.value
                        };
                      })
                    );
                  }}
                />

                <Box sx={{ width: "100px" }}>
                  <OrderButtons
                    isDisabled={{
                      up: !canMoveUp(componentLabel),
                      down: !canMoveDown(componentLabel)
                    }}
                    onClick={{
                      up: () => moveUp(componentLabel),
                      down: () => moveDown(componentLabel)
                    }}
                  />
                </Box>

                <Tooltip title="Attachments cannot be printed">
                  <Checkbox checked={false} sx={{ width: "50px" }} />
                </Tooltip>
              </Box>
            );
          }

          if (componentLabel === SORT_ORDER.EYE) {
            return (
              <Box key={componentLabel} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", my: 1, gap: 2 }}>
                  <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      label={assessmentSettings.componentLabels[componentLabel]?.defaultLabel}
                      control={
                        <Checkbox
                          checked={assessmentSettings.components[componentLabel]}
                          onChange={() => {
                            setShowEye(!assessmentSettings.components.eye);
                            const newState = produce(assessmentSettings, (draft) => {
                              draft.components[componentLabel] =
                                !assessmentSettings.components[componentLabel];
                            });
                            setAssessmentSettings(newState);
                          }}
                        />
                      }
                    />
                  </Box>

                  <LabelInputField
                    value={
                      assessmentSettings.componentLabels[componentLabel]?.customLabel ||
                      assessmentSettings.componentLabels[componentLabel]?.defaultLabel
                    }
                    onFocus={() => setErrorMessage("")}
                    onChange={(e) => {
                      if (e.target.value.length > 40) {
                        setErrorMessage("**Label names can not exceed more than 40 characters");
                        return;
                      }
                      setErrorMessage("");
                      setAssessmentSettings(() =>
                        produce(assessmentSettings, (draft) => {
                          draft.componentLabels[componentLabel] = {
                            ...assessmentSettings.componentLabels[componentLabel],
                            customLabel: e.target.value
                          };
                        })
                      );
                    }}
                  />

                  <Box sx={{ width: "100px" }}>
                    <OrderButtons
                      isDisabled={{
                        up: !canMoveUp(componentLabel),
                        down: !canMoveDown(componentLabel)
                      }}
                      onClick={{
                        up: () => moveUp(componentLabel),
                        down: () => moveDown(componentLabel)
                      }}
                    />
                  </Box>

                  <Checkbox
                    sx={{ width: "50px" }}
                    checked={EyeComponents.every(
                      (eyeComp) =>
                        (assessmentSettings?.showComponentsInPrint?.eye as any)?.[eyeComp.value] ??
                        false
                    )}
                    onChange={() => {
                      const isCurrentlyAllChecked = EyeComponents.every(
                        (eyeComp) =>
                          (assessmentSettings?.showComponentsInPrint?.eye as any)?.[
                            eyeComp.value
                          ] ?? false
                      );

                      const newState = produce(assessmentSettings, (draft) => {
                        if (!draft.showComponentsInPrint) {
                          draft.showComponentsInPrint = {};
                        }

                        const nextState = !isCurrentlyAllChecked;

                        if (
                          typeof draft.showComponentsInPrint.eye === "boolean" ||
                          draft.showComponentsInPrint.eye == null
                        ) {
                          draft.showComponentsInPrint.eye = {};
                        }

                        EyeComponents.forEach((eyeComp) => {
                          (draft.showComponentsInPrint.eye as Record<string, boolean>)[
                            eyeComp.value
                          ] = nextState;
                        });
                      });
                      setAssessmentSettings(newState);
                    }}
                  />
                </Box>

                {showEye && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {EyeComponents.map((eyeComp) => (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        key={eyeComp.value}
                      >
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ flex: 1 }}>{eyeComp.label}</Box>
                        <Box sx={{ width: "100px" }} />
                        <Checkbox
                          sx={{ width: "50px" }}
                          checked={
                            assessmentSettings?.showComponentsInPrint?.eye?.[eyeComp.value] ?? false
                          }
                          onChange={() => {
                            setAssessmentSettings(
                              produce(assessmentSettings, (draft) => {
                                draft.showComponentsInPrint ??= {};
                                if (
                                  typeof draft.showComponentsInPrint.eye === "boolean" ||
                                  draft.showComponentsInPrint.eye == null
                                ) {
                                  draft.showComponentsInPrint.eye = {};
                                }
                                draft.showComponentsInPrint.eye[eyeComp.value] =
                                  !draft.showComponentsInPrint.eye[eyeComp.value];
                              })
                            );
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          }

          return (
            <Box key={componentLabel} sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", my: 1, gap: 2 }}>
                {[SORT_ORDER.LAB_TEST, SORT_ORDER.SERVICE].includes(componentLabel) ? (
                  <Box
                    sx={{
                      flex: 1,
                      width: "100%",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <FormControlLabel
                      label=""
                      control={
                        <Checkbox
                          checked={assessmentSettings.components[componentLabel]}
                          onChange={() => {
                            const newState = produce(assessmentSettings, (draft) => {
                              draft.components[componentLabel] =
                                !assessmentSettings.components[componentLabel];
                            });
                            setAssessmentSettings(newState);
                          }}
                        />
                      }
                    />

                    <FormControlLabel
                      label={assessmentSettings.componentLabels[componentLabel]?.defaultLabel}
                      control={
                        <Radio
                          checked={assessmentSettings.components[componentLabel]}
                          onChange={() => {
                            const newState = produce(assessmentSettings, (draft) => {
                              if (componentLabel === SORT_ORDER.LAB_TEST) {
                                draft.components.labTest = true;
                                draft.components.service = false;
                              } else {
                                draft.components.labTest = false;
                                draft.components.service = true;
                              }
                            });
                            setAssessmentSettings(newState);
                          }}
                        />
                      }
                    />

                    <HtmlTooltip
                      description={
                        componentLabel === SORT_ORDER.LAB_TEST
                          ? "Lab tests will be created from the opd if any test is selected."
                          : "A draft bill will be created with every selected service as item in the bill."
                      }
                    />
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" gap={1} flex={1}>
                    <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                      <FormControlLabel
                        label={assessmentSettings.componentLabels[componentLabel]?.defaultLabel}
                        control={
                          <Checkbox
                            checked={assessmentSettings.components[componentLabel]}
                            onChange={() => {
                              const newState = produce(assessmentSettings, (draft) => {
                                draft.components[componentLabel] =
                                  !assessmentSettings.components[componentLabel];
                              });
                              setAssessmentSettings(newState);
                            }}
                          />
                        }
                      />

                      {componentLabel === SORT_ORDER.OTHER_SERVICES && (
                        <HtmlTooltip description="Shows services except labtest" />
                      )}
                    </Box>
                    {componentLabel === SORT_ORDER.OTHER_SERVICES &&
                      assessmentSettings.components[componentLabel] && (
                        <Box pl={3}>
                          <Typography>Send services to:</Typography>
                          <FormControlLabel
                            label="Bill draft"
                            sx={{ ml: 2 }}
                            control={
                              <Checkbox
                                checked={Boolean(assessmentSettings.sendOtherServicesToBillDraft)}
                                onChange={(_, checked) => {
                                  const newState = produce(assessmentSettings, (draft) => {
                                    draft.sendOtherServicesToBillDraft = checked;
                                    if (checked) {
                                      draft.sendOtherServicesToBillingPackage = false;
                                    }
                                  });
                                  setAssessmentSettings(newState);
                                }}
                              />
                            }
                          />
                          <FormControlLabel
                            label="Billing package"
                            sx={{ ml: 2 }}
                            control={
                              <Checkbox
                                checked={
                                  Boolean(assessmentSettings.sendOtherServicesToBillingPackage) &&
                                  !assessmentSettings.sendOtherServicesToBillDraft
                                }
                                onChange={(_, checked) => {
                                  const newState = produce(assessmentSettings, (draft) => {
                                    draft.sendOtherServicesToBillingPackage = checked;
                                    if (checked) {
                                      draft.sendOtherServicesToBillDraft = false;
                                    }
                                  });
                                  setAssessmentSettings(newState);
                                }}
                              />
                            }
                          />
                        </Box>
                      )}
                  </Box>
                )}

                <LabelInputField
                  value={
                    assessmentSettings.componentLabels[componentLabel]?.customLabel ||
                    assessmentSettings.componentLabels[componentLabel]?.defaultLabel
                  }
                  onFocus={() => setErrorMessage("")}
                  onChange={(e) => {
                    if (e.target.value.length > 40) {
                      setErrorMessage("**Label names can not exceed more than 40 characters");
                      return;
                    }
                    setErrorMessage("");
                    setAssessmentSettings(() =>
                      produce(assessmentSettings, (draft) => {
                        draft.componentLabels[componentLabel] = {
                          ...assessmentSettings.componentLabels[componentLabel],
                          customLabel: e.target.value
                        };
                      })
                    );
                  }}
                />
                {/* opd duration is always needs to be on top and service is tied with labtest, 
              so hiding controls */}
                {!["opdDuration", "service", "authorizedSigners"].includes(componentLabel) ? (
                  <Box sx={{ width: "100px" }}>
                    <OrderButtons
                      isDisabled={{
                        up: !canMoveUp(componentLabel),
                        down: !canMoveDown(componentLabel)
                      }}
                      onClick={{
                        up: () => moveUp(componentLabel),
                        down: () => moveDown(componentLabel)
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ width: "100px" }} />
                )}

                <Checkbox
                  sx={{ width: "50px" }}
                  checked={
                    assessmentSettings.showComponentsInPrint?.[
                      componentLabel as keyof typeof assessmentSettings.showComponentsInPrint
                    ] ?? true
                  }
                  onChange={() => {
                    const newState = produce(assessmentSettings, (draft) => {
                      if (!draft.showComponentsInPrint) {
                        draft.showComponentsInPrint = {};
                      }
                      draft.showComponentsInPrint[
                        componentLabel as keyof typeof assessmentSettings.showComponentsInPrint
                      ] =
                        !assessmentSettings.showComponentsInPrint?.[
                          componentLabel as keyof typeof assessmentSettings.showComponentsInPrint
                        ];
                    });
                    setAssessmentSettings(newState);
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AssessmentLabels;
